import type { OrderDetail } from '@repo/api-contracts';
import { type ReactElement, useState } from 'react';
import { orderShipmentsOf } from '@/entities/order';
import { OrderRefundDialog, OrderRejectDialog } from '@/features/order/cancellation';
import { OrderDelayDialog, OrderProductionDialog } from '@/features/order/production';
import { OrderDeliveredDialog, OrderShipmentForm } from '@/features/order/shipping';
import { koreanDate, type OrderActions } from '@/shared/lib';
import { Button, ContentCard } from '@/shared/ui';

interface OrderProgressActionsProps {
  readonly order: OrderDetail;
  readonly actions: OrderActions;
  readonly onReload: () => void;
  readonly today: string;
  readonly pending?: boolean;
}

type Dialog =
  | 'production'
  | 'delay'
  | 'ship'
  | 'add'
  | 'delivered'
  | 'refund'
  | 'supply'
  | 'reject';

/**
 * 진행 처리 — 주문 상태에 맞는 처리 버튼과 처리 대화상자. 주 동작(제작 착수·출고·배송 완료)은 채운 버튼, 보조 동작은 테두리
 * 버튼, 되돌릴 수 없는 취소·환불은 빨간 버튼으로 둔다. 처리 중 취소 요청이 있으면 제작 착수·출고·공급 불가 취소는 없다.
 * 배송지 변경은 배송지 블록에서(배송지별, 송장 없는 배송지만). 출고·송장 추가는 송장 행마다 배송지를 고른다.
 */
export const OrderProgressActions = ({
  order,
  actions,
  onReload,
  today,
  pending,
}: OrderProgressActionsProps): ReactElement => {
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [openedRequestId, setOpenedRequestId] = useState<string | null>(null);
  const refundPending = order.bankTransfer?.status === 'REFUND_PENDING';
  const paid = order.status === 'PAID' && !refundPending;
  const beforeShip = paid && ['WAITING', 'IN_PRODUCTION'].includes(order.fulfillmentStatus);
  const request = order.cancelRequests.find((item) => item.status === 'REQUESTED');
  const hasRequest = order.pendingCancelRequest !== null || Boolean(request);
  const canStart = paid && order.fulfillmentStatus === 'WAITING' && !hasRequest;
  const canShip = paid && order.fulfillmentStatus === 'IN_PRODUCTION' && !hasRequest;
  const isShipped = paid && order.fulfillmentStatus === 'SHIPPED';
  const canRefund = paid && (hasRequest || order.fulfillmentStatus === 'WAITING');
  const canSupplyCancel = beforeShip && !hasRequest;
  const rejectable = paid && request?.kind === 'CUSTOMER' ? request : undefined;
  const hasAction =
    canStart ||
    canShip ||
    isShipped ||
    beforeShip ||
    canRefund ||
    canSupplyCancel ||
    rejectable !== undefined;
  const close = (): void => {
    setDialog(null);
    setOpenedRequestId(null);
  };
  const common = { orderId: order.id, onReload, onClose: close, pending };
  const destinations = [...order.destinations].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <ContentCard title="진행 처리">
      {refundPending ? (
        <p className="text-sm text-muted-foreground">
          환불 대기 중입니다. 결제 정보에서 환불 완료를 처리해 주세요.
        </p>
      ) : hasAction ? (
        <div className="flex flex-wrap gap-2">
          {canStart ? (
            <Button disabled={pending} onClick={() => setDialog('production')}>
              제작 착수
            </Button>
          ) : null}
          {canShip ? (
            <Button disabled={pending} onClick={() => setDialog('ship')}>
              출고
            </Button>
          ) : null}
          {isShipped ? (
            <>
              <Button disabled={pending || !order.shippedAt} onClick={() => setDialog('delivered')}>
                배송 완료
              </Button>
              <Button variant="outline" disabled={pending} onClick={() => setDialog('add')}>
                송장 추가
              </Button>
            </>
          ) : null}
          {beforeShip ? (
            <Button variant="outline" disabled={pending} onClick={() => setDialog('delay')}>
              지연 안내
            </Button>
          ) : null}
          {rejectable ? (
            <Button
              variant="outline"
              disabled={pending}
              onClick={() => {
                setOpenedRequestId(rejectable.id);
                setDialog('reject');
              }}
            >
              요청 반려
            </Button>
          ) : null}
          {canRefund ? (
            <Button variant="destructive" disabled={pending} onClick={() => setDialog('refund')}>
              취소·환불
            </Button>
          ) : null}
          {canSupplyCancel ? (
            <Button
              variant="ghost"
              className="text-destructive"
              disabled={pending}
              onClick={() => setDialog('supply')}
            >
              공급 불가 취소
            </Button>
          ) : null}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">지금 할 수 있는 처리가 없습니다.</p>
      )}

      {dialog === 'production' ? (
        <OrderProductionDialog
          {...common}
          expectedShipDate={order.expectedShipDate}
          today={today}
          onSubmit={actions.startProduction}
        />
      ) : null}
      {dialog === 'delay' ? (
        <OrderDelayDialog
          {...common}
          expectedShipDate={order.expectedShipDate}
          today={today}
          onSubmit={actions.notifyDelay}
        />
      ) : null}
      {dialog === 'ship' || dialog === 'add' ? (
        <OrderShipmentForm
          {...common}
          destinations={destinations}
          adding={dialog === 'add'}
          existing={orderShipmentsOf(order)}
          onSubmit={dialog === 'add' ? actions.addShipments : actions.ship}
        />
      ) : null}
      {dialog === 'delivered' && order.shippedAt ? (
        <OrderDeliveredDialog
          {...common}
          shippedOn={koreanDate(order.shippedAt)}
          today={today}
          destinations={destinations}
          onSubmit={actions.markDelivered}
        />
      ) : null}
      {dialog === 'refund' || dialog === 'supply' ? (
        <OrderRefundDialog
          {...common}
          hasRequest={hasRequest}
          supplyFailure={dialog === 'supply'}
          onSubmit={actions.refundCancel}
          onSupplySubmit={actions.cancelForSupplyFailure}
          paymentId={order.payment?.id}
        />
      ) : null}
      {dialog === 'reject' && openedRequestId ? (
        <OrderRejectDialog
          requestId={openedRequestId}
          actionable={
            paid &&
            order.cancelRequests.some(
              (item) =>
                item.id === openedRequestId &&
                item.status === 'REQUESTED' &&
                item.kind === 'CUSTOMER',
            )
          }
          onReload={onReload}
          onClose={close}
          pending={pending}
          onSubmit={actions.rejectCancelRequest}
        />
      ) : null}
    </ContentCard>
  );
};
